import Bugsnag from "@bugsnag/js";
import axios from "axios";
import React, { Component } from "react";
import cookie from "react-cookies";
import CurrencyFormat from "react-currency-format";
import SEO from "react-seo-component";
import {
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import LatLngToAddress from "../../components/common/LatLngToAddress";
import DriverRating from "../../components/common/DriverRating";
import constants from "../../Constants";

const baseURL = constants.baseURL;

/* ─── Inline Styles ─── */
const styles = {
  page: {
    background: "linear-gradient(135deg, #f5f7fa 0%, #e4e9f0 100%)",
    minHeight: "100vh",
    paddingTop: 90,
    paddingBottom: 60,
  },
  container: {
    maxWidth: 740,
    margin: "0 auto",
    padding: "0 16px",
  },
  /* header */
  header: {
    textAlign: "center",
    marginBottom: 28,
  },
  headerIcon: {
    width: 48,
    height: 48,
    borderRadius: "50%",
    background: "#34D1BF",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#fff",
    fontSize: 24,
    marginBottom: 12,
  },
  headerTitle: {
    fontSize: 22,
    fontWeight: 700,
    color: "#1a1a2e",
    margin: 0,
  },
  headerSub: {
    fontSize: 13,
    color: "#6b7280",
    marginTop: 4,
  },
  /* search bar */
  searchCard: {
    background: "#fff",
    borderRadius: 16,
    padding: "20px 24px",
    boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
    marginBottom: 20,
  },
  searchRow: {
    display: "flex",
    gap: 10,
    alignItems: "flex-end",
  },
  searchInput: {
    flex: 1,
    border: "1.5px solid #e0e0e0",
    borderRadius: 10,
    padding: "10px 14px",
    fontSize: 14,
    outline: "none",
    transition: "border-color 0.2s",
    fontFamily: "inherit",
    width: "100%",
    boxSizing: "border-box",
  },
  searchBtn: {
    borderRadius: 10,
    padding: "10px 22px",
    fontWeight: 600,
    fontSize: 14,
    border: "none",
    background: "#34D1BF",
    color: "#fff",
    cursor: "pointer",
    whiteSpace: "nowrap",
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    transition: "background 0.2s, transform 0.1s",
    flexShrink: 0,
  },
  /* card generic */
  card: {
    background: "#fff",
    borderRadius: 16,
    padding: "24px",
    boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
    marginBottom: 16,
  },
  cardTitle: {
    fontSize: 13,
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.6px",
    color: "#9ca3af",
    marginBottom: 16,
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  /* status badge */
  statusBadge: (color, bg) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "5px 14px",
    borderRadius: 20,
    fontSize: 12,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    color: color,
    background: bg,
  }),
  /* stepper */
  stepper: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 10,
    position: "relative",
    padding: "0 4px",
  },
  stepperLine: (pct) => ({
    position: "absolute",
    top: 16,
    left: 30,
    right: 30,
    height: 3,
    background: "#e5e7eb",
    borderRadius: 3,
    zIndex: 0,
    overflow: "hidden",
  }),
  stepperProgress: (pct) => ({
    height: "100%",
    width: `${pct}%`,
    background: "#34D1BF",
    borderRadius: 3,
    transition: "width 0.6s ease",
  }),
  stepDot: (active, done) => ({
    width: 32,
    height: 32,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: done ? 16 : 13,
    fontWeight: 700,
    color: done ? "#fff" : active ? "#34D1BF" : "#9ca3af",
    background: done ? "#34D1BF" : active ? "#e0faf7" : "#f3f4f6",
    border: active && !done ? "2px solid #34D1BF" : "2px solid transparent",
    position: "relative",
    zIndex: 1,
    transition: "all 0.3s ease",
  }),
  stepLabel: (active, done) => ({
    fontSize: 10,
    fontWeight: active || done ? 600 : 400,
    color: active || done ? "#1a1a2e" : "#9ca3af",
    textAlign: "center",
    marginTop: 6,
    maxWidth: 70,
  }),
  stepItem: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    flex: 1,
  },
  /* detail grid */
  detailRow: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
    gap: 16,
  },
  detailItem: {
    padding: 0,
  },
  detailLabel: {
    fontSize: 11,
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    color: "#9ca3af",
    marginBottom: 4,
    display: "flex",
    alignItems: "center",
    gap: 5,
  },
  detailValue: {
    fontSize: 14,
    fontWeight: 500,
    color: "#1a1a2e",
    lineHeight: "1.5",
  },
  /* route card */
  routeRow: {
    display: "flex",
    alignItems: "stretch",
    gap: 16,
  },
  routeTimeline: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    paddingTop: 4,
    flexShrink: 0,
    width: 24,
  },
  routeDotFrom: {
    width: 12,
    height: 12,
    borderRadius: "50%",
    background: "#34D1BF",
    border: "2px solid #34D1BF",
    flexShrink: 0,
  },
  routeLine: {
    width: 2,
    flex: 1,
    background: "repeating-linear-gradient(to bottom, #34D1BF 0, #34D1BF 4px, transparent 4px, transparent 8px)",
    margin: "4px 0",
    minHeight: 30,
  },
  routeDotTo: {
    width: 12,
    height: 12,
    borderRadius: 3,
    background: "#ff6b6b",
    border: "2px solid #ff6b6b",
    flexShrink: 0,
  },
  routeDetails: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    gap: 12,
  },
  routePoint: {
    padding: 0,
  },
  routePointLabel: {
    fontSize: 10,
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    color: "#9ca3af",
    marginBottom: 2,
  },
  routePointAddress: {
    fontSize: 14,
    fontWeight: 500,
    color: "#1a1a2e",
    lineHeight: 1.4,
  },
  /* payment card */
  paymentCard: {
    background: "#fff",
    borderRadius: 16,
    padding: "24px",
    boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
    marginBottom: 16,
    textAlign: "center",
  },
  priceAmount: {
    fontSize: 42,
    fontWeight: 800,
    color: "#1a1a2e",
    letterSpacing: "-1px",
    lineHeight: 1.1,
  },
  paymentBadge: (paid) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "6px 16px",
    borderRadius: 20,
    fontSize: 12,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    color: paid ? "#059669" : "#d97706",
    background: paid ? "#ecfdf5" : "#fffbeb",
    marginTop: 8,
  }),
  payBtn: {
    borderRadius: 12,
    padding: "14px 32px",
    fontWeight: 700,
    fontSize: 15,
    border: "none",
    background: "linear-gradient(135deg, #34D1BF 0%, #2bb5a5 100%)",
    color: "#fff",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    marginTop: 16,
    transition: "transform 0.15s, box-shadow 0.15s",
    boxShadow: "0 4px 14px rgba(52,209,191,0.35)",
  },
  bankCard: {
    background: "#f8fafc",
    borderRadius: 12,
    padding: "16px 20px",
    border: "1px dashed #cbd5e1",
    marginTop: 16,
    textAlign: "left",
    display: "inline-block",
  },
  /* help bar */
  helpBar: {
    display: "flex",
    gap: 12,
    justifyContent: "center",
    flexWrap: "wrap",
    marginBottom: 16,
  },
  helpBtn: (bg, hoverBg) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "10px 20px",
    borderRadius: 12,
    fontSize: 13,
    fontWeight: 600,
    color: "#fff",
    background: bg,
    textDecoration: "none",
    transition: "transform 0.15s, background 0.2s",
    border: "none",
    cursor: "pointer",
  }),
  /* refresh */
  refreshBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "8px 18px",
    borderRadius: 10,
    fontSize: 12,
    fontWeight: 600,
    color: "#6b7280",
    background: "#f3f4f6",
    border: "1px solid #e5e7eb",
    cursor: "pointer",
    transition: "all 0.2s",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  /* cancel */
  cancelBtn: {
    fontSize: 12,
    color: "#ef4444",
    background: "transparent",
    border: "1px solid #fecaca",
    borderRadius: 10,
    padding: "8px 18px",
    cursor: "pointer",
    fontWeight: 500,
    transition: "all 0.2s",
  },
  /* invoice */
  invoiceBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "8px 18px",
    borderRadius: 10,
    fontSize: 13,
    fontWeight: 600,
    color: "#4f46e5",
    background: "#eef2ff",
    border: "1px solid #c7d2fe",
    textDecoration: "none",
    cursor: "pointer",
    transition: "all 0.2s",
  },
  invoiceLoading: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    fontSize: 12,
    color: "#9ca3af",
    fontStyle: "italic",
  },
  /* loading spinner */
  spinner: {
    display: "inline-block",
    width: 18,
    height: 18,
    border: "2.5px solid #e5e7eb",
    borderTopColor: "#34D1BF",
    borderRadius: "50%",
    animation: "toSpin 0.6s linear infinite",
  },
  /* vehicle chip */
  vehicleChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 10,
    padding: "8px 14px",
    borderRadius: 12,
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
  },
  /* description box */
  descBox: {
    background: "#f8fafc",
    borderRadius: 12,
    padding: 16,
    border: "1px solid #e2e8f0",
    fontSize: 13,
    lineHeight: 1.6,
    color: "#374151",
  },
  /* alert */
  alertSuccess: {
    background: "#ecfdf5",
    border: "1px solid #a7f3d0",
    borderRadius: 12,
    padding: "12px 18px",
    color: "#065f46",
    fontSize: 14,
    fontWeight: 600,
    display: "flex",
    alignItems: "center",
    gap: 10,
    marginBottom: 16,
  },
  alertDanger: {
    background: "#fef2f2",
    border: "1px solid #fecaca",
    borderRadius: 12,
    padding: "12px 18px",
    color: "#991b1b",
    fontSize: 14,
    fontWeight: 600,
    display: "flex",
    alignItems: "center",
    gap: 10,
    marginBottom: 16,
  },
  alertError: {
    background: "#fef2f2",
    border: "1px solid #fecaca",
    borderRadius: 12,
    padding: "10px 16px",
    color: "#991b1b",
    fontSize: 13,
    marginBottom: 12,
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  /* Driver assigning */
  assigningDriver: {
    background: "#fffbeb",
    border: "1px solid #fde68a",
    borderRadius: 12,
    padding: "14px 18px",
    fontSize: 13,
    color: "#92400e",
    display: "flex",
    alignItems: "flex-start",
    gap: 10,
    lineHeight: 1.5,
  },
  /* rating highlight */
  ratingHighlight: {
    padding: "20px",
    borderRadius: "16px",
    backgroundColor: "#fffde7",
    border: "2px solid #ffc107",
    boxShadow: "0 0 20px rgba(255, 193, 7, 0.3)",
    animation: "toPulse 2s infinite",
  },
  /* cancel modal */
  cancelModalBody: {
    padding: "24px",
  },
  cancelReasonGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
    gap: 10,
    marginBottom: 16,
  },
  cancelReasonBtn: (selected) => ({
    padding: "12px 16px",
    borderRadius: 10,
    border: selected ? "2px solid #ef4444" : "1.5px solid #e5e7eb",
    background: selected ? "#fef2f2" : "#fff",
    color: selected ? "#dc2626" : "#374151",
    fontSize: 13,
    fontWeight: selected ? 600 : 500,
    cursor: "pointer",
    transition: "all 0.15s",
    textAlign: "center",
  }),
  cancelTextarea: {
    width: "100%",
    border: "1.5px solid #e5e7eb",
    borderRadius: 10,
    padding: "10px 14px",
    fontSize: 14,
    resize: "vertical",
    minHeight: 60,
    fontFamily: "inherit",
    marginBottom: 16,
    outline: "none",
  },
  cancelRatingSection: {
    background: "#f8fafc",
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
  },
  cancelRatingLabel: {
    fontSize: 12,
    fontWeight: 600,
    color: "#6b7280",
    marginBottom: 6,
    display: "block",
  },
  cancelStar: (filled, hovered) => ({
    cursor: "pointer",
    fontSize: 28,
    color: filled || hovered ? "#fbbf24" : "#d1d5db",
    transition: "all 0.1s",
    transform: hovered ? "scale(1.15)" : "scale(1)",
    display: "inline-block",
    marginRight: 4,
  }),
  cancelSubmitBtn: {
    width: "100%",
    padding: "14px",
    borderRadius: 12,
    border: "none",
    background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
    color: "#fff",
    fontSize: 15,
    fontWeight: 700,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    transition: "all 0.2s",
  },
  cancelWarning: {
    background: "#fffbeb",
    border: "1px solid #fde68a",
    borderRadius: 10,
    padding: "10px 14px",
    fontSize: 12,
    color: "#92400e",
    marginBottom: 16,
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
};

/* ─── Cancel Reasons ─── */
const CANCEL_REASONS = [
  { value: "changed_mind", label: "Changed my mind" },
  { value: "found_alternative", label: "Found an alternative" },
  { value: "pricing_issue", label: "Pricing issue" },
  { value: "schedule_conflict", label: "Schedule conflict" },
  { value: "driver_delay", label: "Driver taking too long" },
  { value: "wrong_details", label: "Entered wrong details" },
  { value: "other", label: "Other" },
];

/* ─── Status helpers ─── */
const ORDER_STEPS = [
  { key: "in-review", label: "Review", icon: "mdi-clipboard-text-outline" },
  { key: "accepted", label: "Accepted", icon: "mdi-check-circle-outline" },
  { key: "in-progress", label: "In Progress", icon: "mdi-truck-fast-outline" },
  { key: "finished", label: "Delivered", icon: "mdi-flag-checkered" },
];

function getStatusIndex(status) {
  const idx = ORDER_STEPS.findIndex((s) => s.key === status);
  return idx >= 0 ? idx : -1;
}

function getStatusColor(status) {
  const map = {
    "in-review": { color: "#d97706", bg: "#fffbeb" },
    accepted: { color: "#2563eb", bg: "#eff6ff" },
    "in-progress": { color: "#7c3aed", bg: "#f5f3ff" },
    finished: { color: "#059669", bg: "#ecfdf5" },
    stopped: { color: "#ef4444", bg: "#fef2f2" },
  };
  return map[status] || { color: "#6b7280", bg: "#f3f4f6" };
}

function formatDate(dateStr) {
  if (!dateStr) return "";
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-ZA", {
      weekday: "short",
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return dateStr;
  }
}

class Section extends Component {
  constructor() {
    super();

    let orderid = "";
    let email = "";
    let paynow = "";
    let success = "";
    let cancel = "";
    let ratenow = "";
    try {
      const queryParams = new URLSearchParams(window.location.search);
      orderid = queryParams.get("orderid") || "";
      email = queryParams.get("email") || "";
      paynow = queryParams.get("paynow") || "";
      success = queryParams.get("success") || "";
      cancel = queryParams.get("cancel") || "";
      ratenow = queryParams.get("ratenow") || "";
    } catch (error) {
      Bugsnag.notify(error);
    }

    this.state = {
      loading: false,
      ringDriver: false,
      trackedOrderValid: false,
      apierror: "",
      orderid,
      email,
      paynow,
      ratenow,
      orderdetails: {},
      success,
      cancel,
      pdfinvoice: "",
      // Cancel modal state
      showCancelModal: false,
      cancelReason: "",
      cancelReasonOther: "",
      cancelServiceRating: 0,
      cancelDriverRating: 0,
      cancelHoveredService: 0,
      cancelHoveredDriver: 0,
      cancelSubmitting: false,
      cancelError: "",
    };
    this.ratingRef = React.createRef();
  }
  validStepOne() {
    const { orderid, email } = this.state;
    return !(orderid?.length < 2 || email?.length < 5);
  }

  trackOrder(showBankingDetails) {
    const { orderid, email } = this.state;

    this.setState({
      loading: true,
      apierror: "",
      showBankingDetails: undefined,
    });
    axios
      .post(baseURL + "/track-order", {
        orderid,
        email,
      })
      .then((response) => {
        this.setState(
          {
            orderdetails: response.data,
            trackedOrderValid: response.data?.orderid?.length > 0,
            showBankingDetails: showBankingDetails,
          },
          () => {
            if (response.data?.orderid?.length > 0) {
              localStorage.clear();
              cookie.save(
                "currentOrder",
                `email=${response.data.email}&orderid=${response.data.orderid}`,
                {
                  path: "/",
                  maxAge: 86400,
                  secure: true,
                }
              );
              cookie.save("usr-id-qt", response.data?.token, {
                path: "/",
                maxAge: 5 * 60 * 1000, //force 5 minutes
                secure: true,
                domain: ".qiktruck.co.za",
              });
              cookie.save("quotetoken", response.data?.token, {
                path: "/",
                maxAge: 86400,
                secure: true,
              });
              localStorage.setItem("orderid", response.data.orderid);
              axios
                .post(baseURL + "/payment-document", {
                  orderid,
                  token: response.data?.token,
                })
                .then((response) => {
                  setTimeout(() => {
                    this.setState({
                      pdfinvoice: response.data,
                    });
                  }, 10000);
                })
                .catch((e) => {
                  console.log(e);
                  Bugsnag.notify(e);
                });
            }
          }
        );
      })
      .catch((e) => {
        this.setState({ apierror: e.message });
        Bugsnag.notify(e);
      })
      .finally(() => this.setState({ loading: false }));
  }
  cancelOrder() {
    const { orderid } = this.state.orderdetails;
    const {
      cancelReason,
      cancelReasonOther,
      cancelServiceRating,
      cancelDriverRating,
    } = this.state;

    // Validate reason
    if (!cancelReason) {
      this.setState({ cancelError: "Please select a reason for cancellation" });
      return;
    }
    if (cancelReason === "other" && !cancelReasonOther.trim()) {
      this.setState({ cancelError: "Please describe your reason" });
      return;
    }

    const token = cookie.load("usr-id-qt") || cookie.load("quotetoken");
    const reasonText = cancelReason === "other" 
      ? cancelReasonOther.trim() 
      : CANCEL_REASONS.find(r => r.value === cancelReason)?.label || cancelReason;

    this.setState({
      cancelSubmitting: true,
      cancelError: "",
    });

    axios
      .post(
        baseURL + "/customer-stop-order",
        {
          orderid,
          reason: reasonText,
          reasonCode: cancelReason,
          serviceRating: cancelServiceRating || null,
          driverRating: cancelDriverRating || null,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )
      .then((response) => {
        this.setState({ showCancelModal: false });
        window.location.reload();
      })
      .catch((e) => {
        this.setState({ cancelError: e.response?.data?.error || e.message });
        Bugsnag.notify(e);
      })
      .finally(() => this.setState({ cancelSubmitting: false }));
  }
  ringDriver() {
    const orderid = this.state.orderid;

    this.setState({
      loading: true,
      happyDriver: false,
    });

    axios
      .post(baseURL + "/ring-driver", {
        orderid,
      })
      .then((response) => {
        setTimeout(() => {
          this.setState({
            happyDriver: true,
          });
        }, 1000);
      })
      .catch((e) => {
        Bugsnag.notify(e);
      })
      .finally((e) => this.setState({ loading: false }));
  }
  componentDidMount() {
    if (this.state.paynow === "yes") this.trackOrder();
    if (this.state.ratenow === "yes") this.trackOrder();
  }
  componentDidUpdate(prevProps, prevState) {
    // Scroll to rating section when ratenow=yes and order details are loaded
    if (
      this.state.ratenow === "yes" &&
      this.state.orderdetails?.vehicleid &&
      prevState.orderdetails?.vehicleid !== this.state.orderdetails?.vehicleid
    ) {
      setTimeout(() => {
        this.ratingRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 500);
    }
  }
  allowPayment(order) {
    return (
      order.paymentstatus !== "COMPLETE" &&
      (order.orderstatus === "accepted" ||
        order.orderstatus === "finished" ||
        order.orderstatus === "in-progress")
    );
  }

  renderStepper(status) {
    const currentIdx = getStatusIndex(status);
    if (status === "stopped") {
      return (
        <div style={{ ...styles.statusBadge("#ef4444", "#fef2f2"), marginBottom: 8 }}>
          <i className="mdi mdi-close-circle" style={{ fontSize: 14 }}></i>
          Cancelled
        </div>
      );
    }
    const progressPct = currentIdx < 0 ? 0 : (currentIdx / (ORDER_STEPS.length - 1)) * 100;
    return (
      <div style={styles.stepper}>
        <div style={styles.stepperLine(progressPct)}>
          <div style={styles.stepperProgress(progressPct)} />
        </div>
        {ORDER_STEPS.map((step, i) => {
          const done = i <= currentIdx;
          const active = i === currentIdx;
          return (
            <div key={step.key} style={styles.stepItem}>
              <div style={styles.stepDot(active, done)}>
                {done ? <i className="mdi mdi-check" /> : i + 1}
              </div>
              <div style={styles.stepLabel(active, done)}>{step.label}</div>
            </div>
          );
        })}
      </div>
    );
  }

  render() {
    const { orderdetails, success, cancel, ringDriver, happyDriver } =
      this.state;
    const statusColors = getStatusColor(orderdetails.orderstatus);
    const isPaid =
      success === "true" || orderdetails.paymentstatus === "COMPLETE";
    const isCancelled =
      cancel === "true" || orderdetails.paymentstatus === "CANCELLED";

    return (
      <React.Fragment>
        <style>
          {`
            @keyframes toSpin { to { transform: rotate(360deg); } }
            @keyframes toPulse {
              0% { box-shadow: 0 0 15px rgba(255, 193, 7, 0.3); }
              50% { box-shadow: 0 0 30px rgba(255, 193, 7, 0.6); }
              100% { box-shadow: 0 0 15px rgba(255, 193, 7, 0.3); }
            }
            .to-search-input:focus { border-color: #34D1BF !important; }
            .to-search-btn:hover { background: #2bb5a5 !important; transform: translateY(-1px); }
            .to-pay-btn:hover { transform: translateY(-2px); box-shadow: 0 6px 20px rgba(52,209,191,0.4) !important; }
            .to-refresh-btn:hover { background: #e5e7eb !important; color: #374151 !important; }
            .to-cancel-btn:hover { background: #fef2f2 !important; color: #dc2626 !important; border-color: #f87171 !important; }
            .to-invoice-btn:hover { background: #e0e7ff !important; }
            .to-help-btn:hover { transform: translateY(-1px); opacity:0.92; }
          `}
        </style>
        <SEO
          title={
            "On demand truck and driver hire transportation service. Local truck/bakkie hire and couriers at affordable prices."
          }
          titleTemplate={"QikTruck"}
          titleSeparator={`-`}
          description={
            "Move your goods with our delivery vehicles and drivers. Furniture, Equipment, Construction Materials and more! Review your instant online quotation now. On demand truck and driver hire transportation service."
          }
          image={"https://media.qiktruck.co.za/qiktruck-preview-header.png"}
          pathname={"https://qiktruck.co.za/track-order"}
          twitterUsername={"qiktrucksa"}
        />

        <section style={styles.page} id="location">
          <div style={styles.container}>
            {/* Ring Driver Modal – unchanged functionality */}
            <Modal
              isOpen={ringDriver}
              onOpened={() => {
                this.ringDriver();
              }}
            >
              <ModalHeader>
                <i className="mdi mdi-phone-in-talk" style={{ color: "#34D1BF", marginRight: 8 }}></i>
                Ringing Driver
              </ModalHeader>
              <ModalBody>
                <div style={{ textAlign: "center", padding: "20px 0" }}>
                  <div
                    style={{
                      width: 64,
                      height: 64,
                      borderRadius: "50%",
                      background: "#ecfdf5",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginBottom: 16,
                    }}
                  >
                    <i
                      className="mdi mdi-phone-in-talk"
                      style={{ fontSize: 28, color: "#34D1BF" }}
                    ></i>
                  </div>
                  {happyDriver && (
                    <div style={styles.alertSuccess}>
                      <i className="mdi mdi-check-circle" style={{ fontSize: 18 }}></i>
                      Driver notification received.
                    </div>
                  )}
                  <p style={{ color: "#374151", marginBottom: 8 }}>
                    Connecting to the driver...
                  </p>
                  <p style={{ color: "#6b7280", fontSize: 13 }}>
                    The driver will call back once they are free for a phone call.
                  </p>
                  <p
                    style={{
                      color: "#92400e",
                      fontSize: 12,
                      background: "#fffbeb",
                      padding: "10px 14px",
                      borderRadius: 10,
                      marginTop: 12,
                    }}
                  >
                    <i className="mdi mdi-information-outline" style={{ marginRight: 6 }}></i>
                    Drivers cannot accept calls while on trips. Use this once
                    every 20 min.
                  </p>
                </div>
              </ModalBody>
              <ModalFooter style={{ border: "none" }}>
                <Button
                  color="secondary"
                  style={{ borderRadius: 10 }}
                  onClick={() => this.setState({ ringDriver: false })}
                >
                  Close
                </Button>
              </ModalFooter>
            </Modal>

            {/* ─── Header ─── */}
            <div style={styles.header}>
              <div style={styles.headerIcon}>
                <i className="mdi mdi-map-marker-path"></i>
              </div>
              <h1 style={styles.headerTitle}>Track Your Order</h1>
              <p style={styles.headerSub}>
                Enter your order number to view real-time status
              </p>
            </div>

            {/* ─── Search Card ─── */}
            <div style={styles.searchCard}>
              <div style={styles.searchRow}>
                <div style={{ flex: 1 }}>
                  <label
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      color: "#6b7280",
                      marginBottom: 4,
                      display: "block",
                    }}
                  >
                    Order Number
                  </label>
                  <input
                    className="to-search-input"
                    style={styles.searchInput}
                    type="text"
                    placeholder="e.g. 60361608-a189-4d41-..."
                    defaultValue={this.state.orderid}
                    onChange={(e) =>
                      this.setState({ orderid: e.target.value })
                    }
                  />
                </div>
                <button
                  className="to-search-btn"
                  style={{
                    ...styles.searchBtn,
                    opacity:
                      !this.validStepOne() || this.state.loading ? 0.5 : 1,
                    pointerEvents:
                      !this.validStepOne() || this.state.loading
                        ? "none"
                        : "auto",
                  }}
                  onClick={() => this.trackOrder()}
                  disabled={!this.validStepOne() || this.state.loading}
                >
                  {this.state.loading ? (
                    <span style={styles.spinner}></span>
                  ) : (
                    <i className="mdi mdi-magnify" style={{ fontSize: 16 }}></i>
                  )}
                  Track
                </button>
              </div>

              {this.state.loading === false &&
                this.state.apierror.length > 0 && (
                  <div style={{ ...styles.alertError, marginTop: 12 }}>
                    <i className="mdi mdi-alert-circle-outline" style={{ fontSize: 16 }}></i>
                    {this.state.apierror}
                  </div>
                )}
            </div>

            {/* ─── Payment Alerts ─── */}
            {isPaid && (
              <div style={styles.alertSuccess}>
                <i className="mdi mdi-check-circle" style={{ fontSize: 20 }}></i>
                Your payment was successful.
              </div>
            )}
            {isCancelled && (
              <div style={styles.alertDanger}>
                <i className="mdi mdi-close-circle" style={{ fontSize: 20 }}></i>
                Your payment was not processed.
              </div>
            )}

            {/* ─── Order Details ─── */}
            {this.state.trackedOrderValid && (
              <>
                {/* Status + Stepper Card */}
                <div style={styles.card}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      flexWrap: "wrap",
                      gap: 12,
                      marginBottom: 20,
                    }}
                  >
                    <div>
                      <div style={{ fontSize: 12, color: "#9ca3af", marginBottom: 2 }}>
                        Order
                      </div>
                      <div
                        style={{
                          fontSize: 18,
                          fontWeight: 700,
                          color: "#1a1a2e",
                          fontFamily: "monospace",
                        }}
                      >
                        #{orderdetails.orderid.split("-")[0]}
                      </div>
                    </div>
                    <div
                      style={styles.statusBadge(
                        statusColors.color,
                        statusColors.bg
                      )}
                    >
                      <i
                        className={`mdi ${
                          orderdetails.orderstatus === "finished"
                            ? "mdi-check-circle"
                            : orderdetails.orderstatus === "stopped"
                            ? "mdi-close-circle"
                            : "mdi-clock-outline"
                        }`}
                        style={{ fontSize: 14 }}
                      ></i>
                      {orderdetails.orderstatus === "finished"
                        ? "Delivered"
                        : orderdetails.orderstatus === "stopped"
                        ? "Cancelled"
                        : orderdetails.orderstatus?.replace("-", " ")}
                    </div>
                  </div>
                  {this.renderStepper(orderdetails.orderstatus)}
                </div>

                {/* Driver + Vehicle Card */}
                <div style={styles.card}>
                  <div style={styles.cardTitle}>
                    <i className="mdi mdi-account-outline" style={{ fontSize: 16 }}></i>
                    Driver & Vehicle
                  </div>
                  <div style={styles.detailRow}>
                    <div style={styles.detailItem}>
                      <div style={styles.detailLabel}>
                        <i className="mdi mdi-account" style={{ fontSize: 13, color: "#34D1BF" }}></i>
                        Driver
                      </div>
                      <div style={styles.detailValue}>
                        {orderdetails.driverfirstname ? (
                          <span style={{ fontWeight: 600 }}>
                            {orderdetails.driverfirstname}
                          </span>
                        ) : orderdetails.orderstatus !== "stopped" ? (
                          <div style={styles.assigningDriver}>
                            <span style={styles.spinner}></span>
                            <span>
                              Finding a driver near you. The driver will call you
                              once they accept the job.
                            </span>
                          </div>
                        ) : (
                          <span style={{ color: "#9ca3af" }}>N/A</span>
                        )}
                      </div>
                    </div>
                    <div style={styles.detailItem}>
                      <div style={styles.detailLabel}>
                        <i className="mdi mdi-truck" style={{ fontSize: 13, color: "#34D1BF" }}></i>
                        Vehicle
                      </div>
                      <div style={{ ...styles.detailValue }}>
                        <div style={styles.vehicleChip}>
                          <img
                            src={`https://media.qiktruck.co.za/${orderdetails?.trucktype}.png`}
                            alt=""
                            style={{ height: 36, objectFit: "contain" }}
                          />
                          <div>
                            <div style={{ fontWeight: 600, fontSize: 13 }}>
                              {orderdetails.trucktype}
                            </div>
                            {["accepted", "in-progress"].includes(
                              orderdetails.orderstatus
                            ) &&
                              orderdetails.licenseplatenumber && (
                                <div
                                  style={{
                                    fontSize: 11,
                                    color: "#6b7280",
                                    fontFamily: "monospace",
                                  }}
                                >
                                  {orderdetails.licenseplatenumber}
                                </div>
                              )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Route Card */}
                <div style={styles.card}>
                  <div style={styles.cardTitle}>
                    <i className="mdi mdi-map-marker-path" style={{ fontSize: 16 }}></i>
                    Route Details
                  </div>
                  <div style={{ display: "flex", gap: 16, marginBottom: 16, flexWrap: "wrap" }}>
                    <div style={{ flex: "1 1 120px" }}>
                      <div style={styles.detailLabel}>
                        <i className="mdi mdi-calendar" style={{ fontSize: 13, color: "#34D1BF" }}></i>
                        Collection Date
                      </div>
                      <div style={{ ...styles.detailValue, fontWeight: 600 }}>
                        {formatDate(orderdetails.collectiondate)}
                      </div>
                      <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
                        at {orderdetails.collectiontime}
                      </div>
                    </div>
                    {(orderdetails?.runners > 0 || orderdetails?.floors > 0) && (
                      <div style={{ flex: "1 1 120px" }}>
                        <div style={styles.detailLabel}>
                          <i className="mdi mdi-account-group" style={{ fontSize: 13, color: "#34D1BF" }}></i>
                          Extras
                        </div>
                        <div style={styles.detailValue}>
                          {orderdetails?.runners > 0 && (
                            <span>{orderdetails.runners} helper{orderdetails.runners > 1 ? "s" : ""}</span>
                          )}
                          {orderdetails?.runners > 0 && orderdetails?.floors > 0 && " · "}
                          {orderdetails?.floors > 0 && (
                            <span>{orderdetails.floors} floor{orderdetails.floors > 1 ? "s" : ""}</span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  <div style={styles.routeRow}>
                    <div style={styles.routeTimeline}>
                      <div style={styles.routeDotFrom}></div>
                      <div style={styles.routeLine}></div>
                      <div style={styles.routeDotTo}></div>
                    </div>
                    <div style={styles.routeDetails}>
                      <div style={styles.routePoint}>
                        <div style={styles.routePointLabel}>Pickup</div>
                        <div style={styles.routePointAddress}>
                          <LatLngToAddress
                            lat={orderdetails.collectionlat}
                            lng={orderdetails.collectionlng}
                          />
                        </div>
                      </div>
                      <div style={styles.routePoint}>
                        <div style={styles.routePointLabel}>Drop-off</div>
                        <div style={styles.routePointAddress}>
                          <LatLngToAddress
                            lat={orderdetails.deliverylat}
                            lng={orderdetails.deliverylng}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Description */}
                  {orderdetails.description && (
                    <div style={{ marginTop: 16 }}>
                      <div style={styles.detailLabel}>
                        <i className="mdi mdi-text-box-outline" style={{ fontSize: 13, color: "#34D1BF" }}></i>
                        Items Description
                      </div>
                      <div style={styles.descBox}>
                        <div style={{ fontWeight: 600, marginBottom: 4, color: "#1a1a2e" }}>
                          {orderdetails.firstname} {orderdetails.lastname}
                        </div>
                        {orderdetails.description}
                      </div>
                    </div>
                  )}
                </div>

                {/* Payment Card */}
                <div style={styles.paymentCard}>
                  <div style={styles.cardTitle}>
                    <i className="mdi mdi-credit-card-outline" style={{ fontSize: 16 }}></i>
                    Payment
                  </div>
                  <CurrencyFormat
                    value={parseFloat(
                      (orderdetails.sellingprice / 100).toFixed(2)
                    )}
                    displayType={"text"}
                    thousandSeparator={true}
                    prefix={"R"}
                    decimalScale={2}
                    renderText={(value) => (
                      <div style={styles.priceAmount}>{value}</div>
                    )}
                  />
                  {orderdetails.paymentid ? (
                    <div style={styles.paymentBadge(isPaid)}>
                      <i
                        className={`mdi ${
                          isPaid ? "mdi-check-circle" : "mdi-clock-outline"
                        }`}
                        style={{ fontSize: 14 }}
                      ></i>
                      Payment {orderdetails.paymentstatus}
                    </div>
                  ) : (
                    <form
                      action={process.env.REACT_APP_PAYMENT_URL}
                      method="post"
                      style={{ marginTop: 8 }}
                    >
                      <input
                        type="hidden"
                        name="merchant_id"
                        value={
                          process.env.REACT_APP_PAYMENT_MERCHANT_PUBLIC_ID
                        }
                      />
                      <input
                        type="hidden"
                        name="merchant_key"
                        value={
                          process.env.REACT_APP_PAYMENT_MERCHANT_PUBLIC_KEY
                        }
                      />
                      <input
                        type="hidden"
                        name="amount"
                        value={parseFloat(
                          (orderdetails.sellingprice / 100).toFixed(2)
                        )}
                      />
                      <input
                        type="hidden"
                        name="item_name"
                        value={orderdetails.description}
                      />
                      <input
                        type="hidden"
                        name="return_url"
                        value={
                          process.env.REACT_APP_PAYMENT_SUCCESS +
                          "&email=" +
                          orderdetails.email +
                          "&orderid=" +
                          orderdetails.orderid
                        }
                      />
                      <input
                        type="hidden"
                        name="cancel_url"
                        value={
                          process.env.REACT_APP_PAYMENT_CANCEL +
                          "&email=" +
                          orderdetails.email +
                          "&orderid=" +
                          orderdetails.orderid
                        }
                      />
                      <input
                        type="hidden"
                        name="notify_url"
                        value={process.env.REACT_APP_PAYMENT_NOTIFY_URL}
                      />
                      <input
                        type="hidden"
                        name="name_first"
                        value={orderdetails.firstname}
                      />
                      <input
                        type="hidden"
                        name="name_last"
                        value={orderdetails.lastname}
                      />
                      <input
                        type="hidden"
                        name="email_address"
                        value={orderdetails.email}
                      />
                      <input
                        type="hidden"
                        name="cell_number"
                        value={orderdetails.mobilenumber}
                      />
                      <input
                        type="hidden"
                        name="email_confirmation"
                        value="1"
                      />
                      <input
                        type="hidden"
                        name="confirmation_address"
                        value="support@qiktruck.co.za"
                      />
                      <input
                        type="hidden"
                        name="m_payment_id"
                        value={orderdetails.orderid}
                      />
                      <input
                        type="hidden"
                        name="custom_str1"
                        value={orderdetails.orderstatus}
                      />

                      {this.allowPayment(orderdetails) &&
                      success !== "true" ? (
                        <button
                          className="to-pay-btn"
                          type="submit"
                          style={styles.payBtn}
                        >
                          <i className="mdi mdi-lock" style={{ fontSize: 16 }}></i>
                          Pay Securely Online
                          <i className="mdi mdi-arrow-right" style={{ fontSize: 16 }}></i>
                        </button>
                      ) : (
                        <div
                          style={{
                            fontSize: 13,
                            color: "#9ca3af",
                            marginTop: 8,
                            fontStyle: "italic",
                          }}
                        >
                          Payment options available when the driver accepts the
                          job.
                        </div>
                      )}
                    </form>
                  )}

                  {/* Bank transfer option */}
                  {this.allowPayment(orderdetails) && (
                    <div style={styles.bankCard}>
                      <div
                        style={{
                          fontSize: 11,
                          fontWeight: 600,
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                          color: "#9ca3af",
                          marginBottom: 8,
                        }}
                      >
                        Or pay via bank transfer
                      </div>
                      <div style={{ fontSize: 14, color: "#374151" }}>
                        <div style={{ marginBottom: 4 }}>
                          <span style={{ color: "#6b7280" }}>Bank:</span>{" "}
                          <strong>FNB</strong>
                        </div>
                        <div style={{ marginBottom: 4 }}>
                          <span style={{ color: "#6b7280" }}>Account:</span>{" "}
                          <strong style={{ fontFamily: "monospace" }}>
                            62934406986
                          </strong>
                        </div>
                        <div>
                          <span style={{ color: "#6b7280" }}>Reference:</span>{" "}
                          <strong style={{ fontFamily: "monospace" }}>
                            {orderdetails.orderid.split("-")[0]}
                          </strong>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Invoice + Refresh Actions */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    gap: 10,
                    flexWrap: "wrap",
                    marginBottom: 16,
                  }}
                >
                  {!this.state.loading &&
                  this.state.pdfinvoice?.length > 0 ? (
                    <a
                      href={
                        "https://media.admin.qiktruck.co.za/" +
                        orderdetails.orderid +
                        ".pdf"
                      }
                      onClick={() => {
                        setTimeout(() => {
                          window.location.reload();
                        }, 100);
                      }}
                      target={"_blank"}
                      rel="noopener noreferrer"
                      className="to-invoice-btn"
                      style={styles.invoiceBtn}
                    >
                      <i className="mdi mdi-file-pdf-box" style={{ fontSize: 16 }}></i>
                      Download Invoice
                    </a>
                  ) : (
                    <div style={styles.invoiceLoading}>
                      <span style={styles.spinner}></span>
                      Preparing invoice...
                    </div>
                  )}
                  <button
                    className="to-refresh-btn"
                    style={styles.refreshBtn}
                    onClick={() => this.trackOrder()}
                  >
                    <i className="mdi mdi-refresh" style={{ fontSize: 14 }}></i>
                    Refresh
                  </button>
                </div>

                {/* Driver Rating Section */}
                {orderdetails.vehicleid && (
                  <div
                    ref={this.ratingRef}
                    style={{
                      ...styles.card,
                      ...(this.state.ratenow === "yes"
                        ? styles.ratingHighlight
                        : {}),
                    }}
                  >
                    <DriverRating
                      orderid={orderdetails.orderid}
                      driverfirstname={orderdetails.driverfirstname}
                      driverlastname={orderdetails.driverlastname}
                      orderstatus={orderdetails.orderstatus}
                      onRatingSubmitted={(rating) => {
                        console.log("Rating submitted:", rating);
                      }}
                    />
                  </div>
                )}
              </>
            )}

            {/* ─── Help Bar ─── */}
            <div style={styles.helpBar}>
              <a
                className="to-help-btn"
                href="mailto:support@qiktruck.co.za"
                target="_blank"
                rel="noopener noreferrer"
                style={styles.helpBtn("#6366f1")}
              >
                <i className="mdi mdi-email-outline" style={{ fontSize: 16 }}></i>
                Email Support
              </a>
              <a
                className="to-help-btn"
                href="https://wa.me/+27663784460"
                target="_blank"
                rel="noopener noreferrer"
                style={styles.helpBtn("#25D366")}
              >
                <i className="mdi mdi-whatsapp" style={{ fontSize: 16 }}></i>
                WhatsApp
              </a>
            </div>

            {/* ─── Cancel Order ─── */}
            {this.state.trackedOrderValid &&
              orderdetails.paymentstatus !== "COMPLETE" &&
              (this.state.orderdetails.orderstatus === "in-review" ||
                this.state.orderdetails.orderstatus === "accepted" ||
                this.state.orderdetails.orderstatus === "in-progress") && (
                <div style={{ textAlign: "center", marginTop: 8 }}>
                  <button
                    className="to-cancel-btn"
                    style={styles.cancelBtn}
                    onClick={() => this.setState({ 
                      showCancelModal: true,
                      cancelReason: "",
                      cancelReasonOther: "",
                      cancelServiceRating: 0,
                      cancelDriverRating: 0,
                      cancelError: "",
                    })}
                  >
                    <i
                      className="mdi mdi-alert-outline"
                      style={{ marginRight: 6, fontSize: 14 }}
                    ></i>
                    Cancel this order
                  </button>
                </div>
              )}

            {/* ─── Cancel Modal ─── */}
            <Modal
              isOpen={this.state.showCancelModal}
              toggle={() => this.setState({ showCancelModal: false })}
              size="md"
            >
              <ModalHeader toggle={() => this.setState({ showCancelModal: false })}>
                <i className="mdi mdi-alert-circle" style={{ color: "#ef4444", marginRight: 8 }}></i>
                Cancel Order
              </ModalHeader>
              <ModalBody style={styles.cancelModalBody}>
                <div style={styles.cancelWarning}>
                  <i className="mdi mdi-information-outline" style={{ fontSize: 16 }}></i>
                  Your order will be moved back for review. Our team may contact you.
                </div>

                {/* Reason Selection */}
                <div style={{ marginBottom: 16 }}>
                  <label style={styles.cancelRatingLabel}>Why are you cancelling?</label>
                  <div style={styles.cancelReasonGrid}>
                    {CANCEL_REASONS.map((reason) => (
                      <button
                        key={reason.value}
                        type="button"
                        style={styles.cancelReasonBtn(this.state.cancelReason === reason.value)}
                        onClick={() => this.setState({ cancelReason: reason.value, cancelError: "" })}
                      >
                        {reason.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Other reason text */}
                {this.state.cancelReason === "other" && (
                  <div style={{ marginBottom: 16 }}>
                    <label style={styles.cancelRatingLabel}>Please describe your reason</label>
                    <textarea
                      style={styles.cancelTextarea}
                      placeholder="Tell us why you're cancelling..."
                      value={this.state.cancelReasonOther}
                      onChange={(e) => this.setState({ cancelReasonOther: e.target.value, cancelError: "" })}
                      maxLength={500}
                    />
                  </div>
                )}

                {/* Ratings Section */}
                <div style={styles.cancelRatingSection}>
                  {/* Service Rating */}
                  <div style={{ marginBottom: 12 }}>
                    <label style={styles.cancelRatingLabel}>
                      How was the booking experience? (optional)
                    </label>
                    <div>
                      {[1, 2, 3, 4, 5].map((star) => (
                        <span
                          key={star}
                          style={styles.cancelStar(
                            star <= this.state.cancelServiceRating,
                            star <= this.state.cancelHoveredService
                          )}
                          onClick={() => this.setState({ cancelServiceRating: star })}
                          onMouseEnter={() => this.setState({ cancelHoveredService: star })}
                          onMouseLeave={() => this.setState({ cancelHoveredService: 0 })}
                        >
                          ★
                        </span>
                      ))}
                      {this.state.cancelServiceRating > 0 && (
                        <span style={{ fontSize: 12, color: "#6b7280", marginLeft: 8 }}>
                          {this.state.cancelServiceRating}/5
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Driver Rating (only if driver assigned) */}
                  {orderdetails.driverfirstname && (
                    <div>
                      <label style={styles.cancelRatingLabel}>
                        How was {orderdetails.driverfirstname}? (optional)
                      </label>
                      <div>
                        {[1, 2, 3, 4, 5].map((star) => (
                          <span
                            key={star}
                            style={styles.cancelStar(
                              star <= this.state.cancelDriverRating,
                              star <= this.state.cancelHoveredDriver
                            )}
                            onClick={() => this.setState({ cancelDriverRating: star })}
                            onMouseEnter={() => this.setState({ cancelHoveredDriver: star })}
                            onMouseLeave={() => this.setState({ cancelHoveredDriver: 0 })}
                          >
                            ★
                          </span>
                        ))}
                        {this.state.cancelDriverRating > 0 && (
                          <span style={{ fontSize: 12, color: "#6b7280", marginLeft: 8 }}>
                            {this.state.cancelDriverRating}/5
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Error */}
                {this.state.cancelError && (
                  <div style={{ ...styles.alertError, marginBottom: 16 }}>
                    <i className="mdi mdi-alert-circle-outline" style={{ fontSize: 16 }}></i>
                    {this.state.cancelError}
                  </div>
                )}

                {/* Submit Button */}
                <button
                  style={{
                    ...styles.cancelSubmitBtn,
                    opacity: this.state.cancelSubmitting ? 0.6 : 1,
                  }}
                  onClick={() => this.cancelOrder()}
                  disabled={this.state.cancelSubmitting}
                >
                  {this.state.cancelSubmitting ? (
                    <>
                      <span style={styles.spinner}></span>
                      Cancelling...
                    </>
                  ) : (
                    <>
                      <i className="mdi mdi-close-circle" style={{ fontSize: 16 }}></i>
                      Confirm Cancellation
                    </>
                  )}
                </button>
              </ModalBody>
            </Modal>
          </div>
        </section>
      </React.Fragment>
    );
  }
}

export default Section;
